import { Children } from "react";
import { ElementType, GlobalConfig } from "../types/builder";

const generateId = (): string => {
  return Math.random().toString(36).substr(2, 9);
};

const createThankYouHeaderSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as ElementType,
  styles: {
    position: "sticky",
    top: 0,
    left: 0,
    width: "100%",
    zIndex: 1000,
    backgroundColor: "rgba(255,255,255,0.95)",
    boxShadow: "0 2px 10px -2px rgba(0,0,0,0.1)",
    backdropFilter: "blur(5px)",
    padding: "10px 0",
    display: "flex",
    justifyContent: "center",
  },
  children: [
    {
      id: generateId(),
      type: "container" as ElementType,
      styles: {
        width: "100%",
        maxWidth: "1334px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "64px",
        position: "relative",
        padding: "0 32px",
      },
      responsiveStyles: {
        tablet: { gap: "20px", padding: "0 16px" },
        mobile: { gap: "12px", padding: "0 8px" },
      },
      children: [
        // Logo
        {
          id: generateId(),
          type: "logo" as ElementType,
          styles: {
            maxWidth: "209px",
            width: "100%",
            display: "flex",
            alignItems: "center",
          },
          responsiveStyles: {
            tablet: { maxWidth: "150px" },
            mobile: { maxWidth: "120px" },
          },
          children: [
            {
              id: generateId(),
              type: "image" as ElementType,
              src: "/fortigold/logo-gold.webp",
              alt: "FortiGold Logo",
              styles: {
                width: "100%",
                height: "100%",
                objectFit: "contain",
              },
            },
          ],
        },
        // Contact Us Button
        {
          id: generateId(),
          type: "link" as ElementType,
          content: "Contact Us",
          href: "/contact-us",
          styles: {
            textTransform: "uppercase",
            backgroundImage:
              "conic-gradient(from 54deg at 28% 100%, #FBBC04 5.4deg, #FED159 196.2deg, #FED159 360deg)",
            color: "#000",
            padding: "16px",
            fontSize: "20px",
            fontWeight: 700,
            borderRadius: "16px",
            border: "1px solid #0000001A",
            transition: "all 0.2s cubic-bezier(.4,0,.2,1)",
            textDecoration: "none",
            display: "inline-block",
          },
          responsiveStyles: {
            tablet: { fontSize: "18px", padding: "12px" },
            mobile: { fontSize: "16px", padding: "10px" },
          },
        },
      ],
    },
  ],
});

const createThankYouImageHeaderSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as ElementType,
  styles: {
    backgroundColor: "#F0F0F1",
    paddingTop: "40px",
    width: "100%",
    display: "flex",
    justifyContent: "center",
  },
  responsiveStyles: {
    tablet: { paddingTop: "20px" },
    mobile: { paddingTop: "20px" },
  },
  children: [
    {
      id: generateId(),
      type: "container" as ElementType,
      styles: {
        width: "100%",
        maxWidth: "1334px",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-end",
        padding: "0 16px",
      },
      responsiveStyles: {
        mobile: { padding: "0" },
      },
      children: [
        {
          id: generateId(),
          type: "container" as ElementType,
          styles: {
            width: "100%",
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "flex-start",
            height: "auto",
            position: "relative",
          },
          responsiveStyles: {
            tablet: { height: "auto" },
            mobile: { height: "auto" },
          },
          children: [
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                width: "100%",
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "flex-start",
                height: "auto",
                position: "relative",
              },
              children: [
                {
                  id: generateId(),
                  type: "image" as ElementType,
                  src: "/fortigold/thankyou-card.webp",
                  alt: "Thank You Header",
                  styles: {
                    maxWidth: "900px",
                    width: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  },
                  responsiveStyles: {
                    tablet: { maxWidth: "600px" },
                    mobile: { maxWidth: "360px" },
                  },
                },
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    position: "absolute",
                    inset: "20% 0 0",
                  },
                  responsiveStyles: {
                    tablet: { inset: "20% 50px 0" },
                    mobile: { inset: "20% 50px 0" },
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        maxWidth: "600px",
                        transform: "translateX(-25%)",
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        gap: "30px",
                      },
                      responsiveStyles: {
                        tablet: {
                          maxWidth: "400px",
                          gap: "16px",
                        },
                        mobile: {
                          maxWidth: "250px",
                          gap: "4px",
                        },
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          styles: {
                            maxWidth: "600px",
                            // transform: "translateX(-25%)",
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexDirection: "column",
                            gap: "18px",
                          },
                          responsiveStyles: {
                            tablet: {
                              maxWidth: "400px",
                              gap: "8px",
                            },
                            mobile: {
                              maxWidth: "250px",
                              gap: "6px",
                            },
                          },
                          children: [
                            {
                              id: generateId(),
                              type: "image" as ElementType,
                              src: "/fortigold/logo-gold.webp",
                              alt: "Thank You Header",
                              styles: {
                                maxWidth: "282px",
                                width: "100%",
                                maxHeight: "100%",
                                objectFit: "contain",
                              },
                              responsiveStyles: {
                                tablet: { maxWidth: "220px" },
                                mobile: { maxWidth: "100px" },
                              },
                            },
                            {
                              id: generateId(),
                              type: "heading" as ElementType,
                              content:
                                "Thank you for your order <br> We have received it",
                              styles: {
                                fontFamily: "SF Pro Display",
                                fontSize: "52px",
                                lineHeight: "57.121px",
                                fontWeight: "700",
                                color: "#231F20",
                                margin: "0",
                                textAlign: "center",
                              },
                              responsiveStyles: {
                                tablet: {
                                  fontSize: "32px",
                                  lineHeight: "32px",
                                },
                                mobile: {
                                  fontSize: "18px",
                                  lineHeight: "20px",
                                },
                              },
                            },
                          ],
                        },
                        {
                          id: generateId(),
                          type: "paragraph" as ElementType,
                          content:
                            "Your order has been received and is now being processed. We’re preparing your FortiGold shipment with care and you’ll receive tracking details soon via email.",
                          styles: {
                            fontFamily: "SF Pro Display",
                            fontSize: "25px",
                            lineHeight: "30px",
                            fontWeight: "400",
                            color: "#231F20",
                            margin: "0",
                            textAlign: "center",
                            transform: "translateX(-4%)",
                          },
                          responsiveStyles: {
                            tablet: {
                              fontSize: "20px",
                              lineHeight: "25px",
                            },
                            mobile: {
                              fontSize: "14px",
                              lineHeight: "18px",
                            },
                          },
                        },
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          styles: {
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexDirection: "column",
                            gap: "18px",
                          },
                          responsiveStyles: {
                            tablet: { gap: "8px" },
                            mobile: { gap: "4px" },
                          },
                          children: [
                            {
                              id: generateId(),
                              type: "image" as ElementType,
                              src: "/fortigold/signature-doctor.webp",
                              alt: "Thank You Header",
                              styles: {
                                maxWidth: "275px",
                                width: "100%",
                                maxHeight: "100%",
                                objectFit: "contain",
                                marginTop: "14px",
                              },
                              responsiveStyles: {
                                tablet: { maxWidth: "200px", marginTop: "0px" },
                                mobile: { maxWidth: "100px", marginTop: "8px" },
                              },
                            },
                            {
                              id: generateId(),
                              type: "paragraph" as ElementType,
                              content: "RESEARCH SCIENTIST",
                              styles: {
                                fontFamily: "SF Pro Display",
                                fontSize: "16px",
                                lineHeight: "100%",
                                fontWeight: "400",
                                color: "#231F20",
                                margin: "0",
                                textAlign: "center",
                              },
                              responsiveStyles: {
                                tablet: { fontSize: "16px" },
                                mobile: { fontSize: "8px" },
                              },
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              id: generateId(),
              type: "image" as ElementType,
              src: "/fortigold/thankyou-doctor.webp",
              alt: "Thank You Header",
              styles: {
                maxWidth: "774px",
                width: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                position: "absolute",
                right: "0",
                transform: "translateX(8%)",
              },
              responsiveStyles: {
                tablet: { maxWidth: "350px" },
                mobile: { maxWidth: "150px" },
              },
            },
          ],
        },
      ],
    },
  ],
});

const createThankYouCardsSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as ElementType,
  styles: {
    backgroundColor: "background",
    padding: "40px 0 60px",
    width: "100%",
    display: "flex",
    justifyContent: "center",
  },
  responsiveStyles: {
    tablet: { padding: "25px 0 40px" },
    mobile: { padding: "20px 0 30px" },
  },
  children: [
    {
      id: generateId(),
      type: "container" as ElementType,
      styles: {
        maxWidth: "1400px",
        margin: "0 auto",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      },
      responsiveStyles: {
        tablet: { maxWidth: "95%" },
        mobile: { maxWidth: "95%" },
      },
      children: [
        // Important note
        {
          id: generateId(),
          type: "container" as ElementType,
          styles: {
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            paddingBottom: "30px",
            width: "100%",
          },
          responsiveStyles: {
            tablet: { paddingBottom: "20px" },
          },
          children: [
            {
              id: generateId(),
              type: "paragraph" as ElementType,
              content:
                "<b>Important:</b> Please note that your payment will appear as Digistore24 on your bank or credit card statement.",
              styles: {
                color: "#000",
                fontFamily: "SF Pro Display, Poppins, sans-serif",
                fontSize: "28px",
                fontWeight: 500,
                lineHeight: "100%",
                textAlign: "center",
                margin: 0,
              },
              responsiveStyles: {
                tablet: { fontSize: "24px" },
                mobile: { fontSize: "18px" },
              },
            },
          ],
        },
        // Cards container
        {
          id: generateId(),
          type: "container" as ElementType,
          styles: {
            display: "flex",
            flexWrap: "wrap",
            alignItems: "stretch",
            justifyContent: "center",
            gap: "40px 50px",
            width: "100%",
            fontFamily: "SF Pro Display, Poppins, sans-serif",
          },
          responsiveStyles: {
            tablet: { gap: "30px" },
          },
          children: [
            // Card 1: Shipping Time
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                padding: "50px",
                borderRadius: "16px",
                border: "1px solid #D1D3D4",
                backgroundColor: "background",
                flex: "1 1 calc(50% - 25px)",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                gap: "45px",
                minWidth: "320px",
                maxWidth: "600px",
              },
              responsiveStyles: {
                tablet: { padding: "30px", gap: "24px" },
                mobile: { padding: "15px", gap: "16px", flex: "1 1 100%" },
              },
              children: [
                // Header
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: "8px",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "image" as ElementType,
                      src: "/fortigold/icon1.webp",
                      alt: "Shipping Icon",
                      styles: {
                        width: "90px",
                        height: "90px",
                        objectFit: "contain",
                      },
                      responsiveStyles: {
                        tablet: { width: "60px", height: "60px" },
                        mobile: { width: "40px", height: "40px" },
                      },
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content: "Shipping Time",
                      styles: {
                        color: "#000",
                        fontSize: "50px",
                        fontWeight: 700,
                        lineHeight: "100%",
                        margin: 0,
                      },
                      responsiveStyles: {
                        tablet: { fontSize: "36px" },
                        mobile: { fontSize: "30px" },
                      },
                    },
                  ],
                },
                // Content
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {},
                  children: [
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content:
                        "Most orders arrive within <b>5-7 business days</b> (U.S) <b>or 10-15 days internationally.</b>",
                      styles: {
                        color: "#231F20",
                        fontSize: "26.4px",
                        fontWeight: 400,
                        lineHeight: "100%",
                        margin: 0,
                      },
                      responsiveStyles: {
                        tablet: { fontSize: "24px" },
                        mobile: { fontSize: "18px" },
                        desktop: { fontSize: "26.4px" },
                      },
                    },
                  ],
                },
              ],
            },
            // Card 2: Bonus Delivery
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                padding: "50px",
                borderRadius: "16px",
                border: "1px solid #D1D3D4",
                backgroundColor: "background",
                flex: "1 1 calc(50% - 25px)",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                gap: "45px",
                minWidth: "320px",
                maxWidth: "600px",
              },
              responsiveStyles: {
                tablet: { padding: "30px", gap: "24px" },
                mobile: { padding: "15px", gap: "16px", flex: "1 1 100%" },
              },
              children: [
                // Header
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: "8px",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "image" as ElementType,
                      src: "/fortigold/icon2.webp",
                      alt: "Bonus Delivery Icon",
                      styles: {
                        width: "90px",
                        height: "90px",
                        objectFit: "contain",
                      },
                      responsiveStyles: {
                        tablet: { width: "60px", height: "60px" },
                        mobile: { width: "40px", height: "40px" },
                      },
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content: "Bonus Delivery",
                      styles: {
                        color: "#000",
                        fontSize: "50px",
                        fontWeight: 700,
                        lineHeight: "100%",
                        margin: 0,
                      },
                      responsiveStyles: {
                        tablet: { fontSize: "36px" },
                        mobile: { fontSize: "30px" },
                      },
                    },
                  ],
                },
                // Content
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {},
                  children: [
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content:
                        "If your order included bonus ebooks, you'll find them in your inbox shortly. Be sure to check your spam or promotions folder just in case.",
                      styles: {
                        color: "#231F20",
                        fontSize: "26.4px",
                        fontWeight: 400,
                        lineHeight: "100%",
                        margin: 0,
                      },
                      responsiveStyles: {
                        tablet: { fontSize: "24px" },
                        mobile: { fontSize: "18px" },
                        desktop: { fontSize: "26.4px" },
                      },
                    },
                  ],
                },
              ],
            },
            // Card 3: Need Help?
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                padding: "50px",
                borderRadius: "16px",
                border: "1px solid #D1D3D4",
                backgroundColor: "background",
                flex: "1 1 calc(50% - 25px)",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                gap: "45px",
                minWidth: "320px",
                maxWidth: "600px",
              },
              responsiveStyles: {
                tablet: { padding: "30px", gap: "24px" },
                mobile: { padding: "15px", gap: "16px", flex: "1 1 100%" },
              },
              children: [
                // Header
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: "8px",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "image" as ElementType,
                      src: "/fortigold/icon3.webp",
                      alt: "Need Help Icon",
                      styles: {
                        width: "90px",
                        height: "90px",
                        objectFit: "contain",
                      },
                      responsiveStyles: {
                        tablet: { width: "60px", height: "60px" },
                        mobile: { width: "40px", height: "40px" },
                      },
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content: "Need Help?",
                      styles: {
                        color: "#000",
                        fontSize: "50px",
                        fontWeight: 700,
                        lineHeight: "100%",
                        margin: 0,
                      },
                      responsiveStyles: {
                        tablet: { fontSize: "36px" },
                        mobile: { fontSize: "30px" },
                      },
                    },
                  ],
                },
                // Content
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    justifyContent: "flex-start",
                    gap: "16px",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content:
                        "Have questions about your order or need support? Our friendly team is ready to help:",
                      styles: {
                        color: "#231F20",
                        fontSize: "26.4px",
                        fontWeight: 400,
                        lineHeight: "100%",
                        margin: 0,
                      },
                      responsiveStyles: {
                        tablet: { fontSize: "24px" },
                        mobile: { fontSize: "18px" },
                        desktop: { fontSize: "26.4px" },
                      },
                    },
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        justifyContent: "flex-start",
                        gap: "8px",
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "paragraph" as ElementType,
                          content:
                            '<b>Email:</b> <a href="mailto:support@fortigold.com">support@fortigold.com</a>',
                          styles: {
                            color: "#231F20",
                            fontSize: "26.4px",
                            fontWeight: 400,
                            lineHeight: "100%",
                            margin: 0,
                          },
                          responsiveStyles: {
                            tablet: { fontSize: "24px" },
                            mobile: { fontSize: "18px" },
                            desktop: { fontSize: "26.4px" },
                          },
                        },
                        {
                          id: generateId(),
                          type: "paragraph" as ElementType,
                          content: "<b>Hours:</b> Monday - Friday, 9AM-5PM EST",
                          styles: {
                            color: "#231F20",
                            fontSize: "26.4px",
                            fontWeight: 400,
                            lineHeight: "100%",
                            margin: 0,
                          },
                          responsiveStyles: {
                            tablet: { fontSize: "24px" },
                            mobile: { fontSize: "18px" },
                            desktop: { fontSize: "26.4px" },
                          },
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            // Card 4: What's Next?
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                padding: "50px",
                borderRadius: "16px",
                border: "1px solid #D1D3D4",
                backgroundColor: "background",
                flex: "1 1 calc(50% - 25px)",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                gap: "45px",
                minWidth: "320px",
                maxWidth: "600px",
              },
              responsiveStyles: {
                tablet: { padding: "30px", gap: "24px" },
                mobile: { padding: "15px", gap: "16px", flex: "1 1 100%" },
              },
              children: [
                // Header
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: "8px",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "image" as ElementType,
                      src: "/fortigold/icon4.webp",
                      alt: "What's Next Icon",
                      styles: {
                        width: "90px",
                        height: "90px",
                        objectFit: "contain",
                      },
                      responsiveStyles: {
                        tablet: { width: "60px", height: "60px" },
                        mobile: { width: "40px", height: "40px" },
                      },
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content: "What's Next?",
                      styles: {
                        color: "#000",
                        fontSize: "50px",
                        fontWeight: 700,
                        lineHeight: "100%",
                        margin: 0,
                      },
                      responsiveStyles: {
                        tablet: { fontSize: "36px" },
                        mobile: { fontSize: "30px" },
                      },
                    },
                  ],
                },
                // Content
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {},
                  children: [
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content:
                        "While you wait for your delivery, take a few moments to read our welcome guide and learn how to get the most from FortiGold. You'll find it in your bonus email!<br>Want health tips, product updates, and exclusive offers? ",
                      styles: {
                        color: "#231F20",
                        fontSize: "26.4px",
                        fontWeight: 400,
                        lineHeight: "100%",
                        margin: 0,
                      },
                      responsiveStyles: {
                        tablet: { fontSize: "24px" },
                        mobile: { fontSize: "18px" },
                        desktop: { fontSize: "26.4px" },
                      },
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ],
});

const createThankYouNewsletterSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as ElementType,
  styles: {
    position: "relative",
    fontFamily: "SF Pro Display",
    width: "100%",
    overflow: "hidden",
    padding: "100px 0 40px",
  },
  responsiveStyles: {
    tablet: { padding: "65px 0 40px" },
    mobile: { padding: "65px 0 24px" },
  },
  children: [
    {
      id: generateId(),
      type: "container" as ElementType,
      styles: {
        position: "relative",
        padding: "150px 0 80px",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundImage:
          "linear-gradient(to bottom, #789332, #2A7034 30%, #00442A 90%)",
      },
      responsiveStyles: {
        tablet: { padding: "100px 0 60px" },
        mobile: { padding: "100px 0 40px" },
      },
      children: [
        // Strip image
        {
          id: generateId(),
          type: "image" as ElementType,
          src: "/fortigold/clip-paths.webp",
          alt: "Thank You Strip",
          styles: {
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            width: "100%",
            height: "32px",
            objectFit: "cover",
            zIndex: 1,
          },
        },
        // Congratulations badge
        {
          id: generateId(),
          type: "image" as ElementType,
          src: "/fortigold/congratualtions.webp",
          alt: "Congratulations Badge",
          styles: {
            position: "absolute",
            top: 0,
            left: "50%",
            transform: "translate3d(-50%, -40%, 0)",
            width: "100%",
            maxWidth: "323px",
            objectFit: "contain",
            zIndex: 2,
          },
          responsiveStyles: {
            tablet: { maxWidth: "220px" },
            mobile: { maxWidth: "220px" },
          },
        },
        // Rhombus
        {
          id: generateId(),
          type: "container" as ElementType,
          styles: {
            width: "50px",
            height: "50px",
            backgroundColor: "#00442A",
            position: "absolute",
            bottom: 0,
            left: "50%",
            transform: "translate3d(-50%, 50%, 0) rotate(45deg)",
            zIndex: 3,
          },
          responsiveStyles: {
            mobile: { width: "30px", height: "30px" },
          },
        },
        // Newsletter Box
        {
          id: generateId(),
          type: "container" as ElementType,
          styles: {
            position: "relative",
            zIndex: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: "95px",
            width: "100%",
            maxWidth: "1200px",
            margin: "0 auto",
          },
          responsiveStyles: {
            tablet: { gap: "45px", maxWidth: "95%" },
            mobile: { gap: "18px", maxWidth: "95%" },
          },
          children: [
            // Header
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: "8px",
                width: "100%",
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as ElementType,
                  content: "You're Officially One of Us",
                  styles: {
                    color: "#FFF",
                    fontSize: "50px",
                    fontWeight: 700,
                    lineHeight: "100%",
                    margin: 0,
                  },
                  responsiveStyles: {
                    mobile: { fontSize: "30px", textAlign: "center" },
                  },
                },
                {
                  id: generateId(),
                  type: "paragraph" as ElementType,
                  content:
                    "You didn't just place an order. You made a commitment to better health - and we don't take that lightly. From now on, you'll be treated like one of our VIP insiders:",
                  styles: {
                    color: "#FFF",
                    textAlign: "center",
                    fontSize: "28px",
                    fontWeight: 300,
                    lineHeight: "100%",
                    margin: 0,
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "24px" },
                    mobile: { fontSize: "18px", textAlign: "center" },
                  },
                },
              ],
            },
            // Content
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: "45px",
                width: "100%",
              },
              responsiveStyles: {
                tablet: { gap: "24px" },
              },
              children: [
                // Cards
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gap: "50px",
                    alignItems: "flex-start",
                    width: "100%",
                  },
                  responsiveStyles: {
                    tablet: { gap: "30px" },
                    mobile: {
                      gridTemplateColumns: "repeat(2, 1fr)",
                      gap: "24px 16px",
                    },
                  },
                  children: [
                    // Card 1: Early access
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        gap: "10px",
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "image" as ElementType,
                          src: "/fortigold/thankYouNewsletterSection1.svg",
                          alt: "Early access to new formulas",
                          styles: { width: "104px", height: "107px" },
                          responsiveStyles: {
                            tablet: {
                              maxWidth: "60px",
                              maxHeight: "60px",
                              width: "100%",
                            },
                            mobile: {
                              maxWidth: "60px",
                              maxHeight: "60px",
                              width: "100%",
                            },
                          },
                        },
                        {
                          id: generateId(),
                          type: "paragraph" as ElementType,
                          content: "Early access to new formulas",
                          styles: {
                            color: "#FFF",
                            textAlign: "center",
                            fontSize: "28px",
                            fontWeight: 300,
                            lineHeight: "33.6px",
                            margin: 0,
                          },
                          responsiveStyles: {
                            tablet: { fontSize: "24px" },
                            mobile: { fontSize: "18px", lineHeight: "24px" },
                          },
                        },
                      ],
                    },
                    // Card 2: Priority Support
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        gap: "10px",
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "image" as ElementType,
                          src: "/fortigold/thankYouNewsletterSection2.svg",
                          alt: "Early access to new formulas",
                          styles: { width: "104px", height: "107px" },
                          responsiveStyles: {
                            tablet: {
                              maxWidth: "60px",
                              maxHeight: "60px",
                              width: "100%",
                            },
                            mobile: {
                              maxWidth: "60px",
                              maxHeight: "60px",
                              width: "100%",
                            },
                          },
                        },
                        {
                          id: generateId(),
                          type: "paragraph" as ElementType,
                          content: "Priority Support",
                          styles: {
                            color: "#FFF",
                            textAlign: "center",
                            fontSize: "28px",
                            fontWeight: 300,
                            lineHeight: "33.6px",
                            margin: 0,
                          },
                          responsiveStyles: {
                            tablet: { fontSize: "24px" },
                            mobile: { fontSize: "18px", lineHeight: "24px" },
                          },
                        },
                      ],
                    },
                    // Card 3: VIP Bundles
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        gap: "10px",
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "image" as ElementType,
                          src: "/fortigold/thankYouNewsletterSection3.svg",
                          alt: "Early access to new formulas",
                          styles: { width: "104px", height: "107px" },
                          responsiveStyles: {
                            tablet: {
                              maxWidth: "60px",
                              maxHeight: "60px",
                              width: "100%",
                            },
                            mobile: {
                              maxWidth: "60px",
                              maxHeight: "60px",
                              width: "100%",
                            },
                          },
                        },
                        {
                          id: generateId(),
                          type: "paragraph" as ElementType,
                          content: "Special VIP only bundles",
                          styles: {
                            color: "#FFF",
                            textAlign: "center",
                            fontSize: "28px",
                            fontWeight: 300,
                            lineHeight: "33.6px",
                            margin: 0,
                          },
                          responsiveStyles: {
                            tablet: { fontSize: "24px" },
                            mobile: { fontSize: "18px", lineHeight: "24px" },
                          },
                        },
                      ],
                    },
                    // Card 4: Monthly health drop-ins
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        gap: "10px",
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "image" as ElementType,
                          src: "/fortigold/thankYouNewsletterSection4.svg",
                          alt: "Early access to new formulas",
                          styles: { width: "104px", height: "107px" },
                          responsiveStyles: {
                            tablet: {
                              maxWidth: "60px",
                              maxHeight: "60px",
                              width: "100%",
                            },
                            mobile: {
                              maxWidth: "60px",
                              maxHeight: "60px",
                              width: "100%",
                            },
                          },
                        },
                        {
                          id: generateId(),
                          type: "paragraph" as ElementType,
                          content:
                            "Monthly health drop-ins from Dr. James Mitchell himself",
                          styles: {
                            color: "#FFF",
                            textAlign: "center",
                            fontSize: "28px",
                            fontWeight: 300,
                            lineHeight: "33.6px",
                            margin: 0,
                          },
                          responsiveStyles: {
                            tablet: { fontSize: "24px" },
                            mobile: { fontSize: "18px", lineHeight: "24px" },
                          },
                        },
                      ],
                    },
                  ],
                },
                // Bottom text
                {
                  id: generateId(),
                  type: "paragraph" as ElementType,
                  content:
                    "Welcome to the FortiGold family. We're in this with you - every step of the way.",
                  styles: {
                    color: "#FFF",
                    fontSize: "30px",
                    fontWeight: 500,
                    lineHeight: "100%",
                    textAlign: "center",
                    margin: 0,
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "24px" },
                    mobile: { fontSize: "18px" },
                  },
                },
              ],
            },
          ],
        },
      ],
    },
  ],
});

const createGetReadySection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as ElementType,
  styles: {
    backgroundColor: "#E6E7E8",
    padding: "100px 0",
    fontFamily: "SF Pro Display",
    width: "100%",
    display: "flex",
    justifyContent: "center",
    marginTop: "-40px",
  },
  responsiveStyles: {
    tablet: { padding: "60px 0", marginTop: "-40px" },
    mobile: { padding: "40px 0", marginTop: "-24px" },
  },
  children: [
    {
      id: generateId(),
      type: "container" as ElementType,
      styles: {
        maxWidth: "1368px",
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        gap: "40px",
        width: "100%",
        padding: "0 10px",
      },
      responsiveStyles: {
        tablet: { gap: "16px", padding: "0 20px" },
        mobile: { gap: "16px", padding: "0 20px" },
      },
      children: [
        // Box 1
        {
          id: generateId(),
          type: "container" as ElementType,
          styles: {
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            flexDirection: "column",
            gap: "70px",
            maxWidth: "1280px",
            margin: "0 auto",
            width: "100%",
          },
          responsiveStyles: {
            tablet: { gap: "45px" },
            mobile: { gap: "24px" },
          },
          children: [
            // Header
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                gap: "20px",
              },
              children: [
                {
                  id: generateId(),
                  type: "image" as ElementType,
                  src: "/fortigold/phone.svg",
                  alt: "Phone Icon",
                  styles: {
                    width: "65px",
                    height: "65px",
                    objectFit: "contain",
                  },
                  responsiveStyles: {
                    tablet: { width: "40px", height: "40px" },
                    mobile: { width: "30px", height: "30px" },
                  },
                },
                {
                  id: generateId(),
                  type: "heading" as ElementType,
                  content: "Get Ready - Your VIP Wellness Call is Coming",
                  styles: {
                    color: "#000",
                    fontSize: "58.3px",
                    fontWeight: 700,
                    lineHeight: "100%",
                    margin: 0,
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "40px" },
                    mobile: { fontSize: "30px" },
                  },
                },
              ],
            },
            // Content
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: "20px",
                width: "100%",
              },
              responsiveStyles: {
                mobile: { gap: "16px" },
              },
              children: [
                {
                  id: generateId(),
                  type: "paragraph" as ElementType,
                  content:
                    "You've just joined the FortiGold inner circle - and that means you qualify for a 1-on-1 VIP Support Call with our wellness team.",
                  styles: {
                    color: "#231F20",
                    fontSize: "28px",
                    fontWeight: 400,
                    lineHeight: "33.6px",
                    margin: 0,
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "24px" },
                    mobile: { fontSize: "18px", lineHeight: "26px" },
                  },
                },
                {
                  id: generateId(),
                  type: "paragraph" as ElementType,
                  content:
                    "We'll check in to welcome you personally, answer any questions you have about FortiGold, and help you get the absolute best results possible.",
                  styles: {
                    color: "#231F20",
                    fontSize: "28px",
                    fontWeight: 400,
                    lineHeight: "33.6px",
                    margin: 0,
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "24px" },
                    mobile: { fontSize: "18px", lineHeight: "26px" },
                  },
                },
                // List
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: { width: "100%" },
                  children: [
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content: "What to expect:",
                      styles: {
                        color: "#231F20",
                        fontSize: "28px",
                        fontWeight: 700,
                        margin: 0,
                      },
                      responsiveStyles: {
                        tablet: { fontSize: "24px" },
                        mobile: { fontSize: "18px" },
                      },
                    },
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                        margin: 0,
                        padding: 0,
                        listStyle: "none",
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          styles: {
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          },
                          children: [
                            {
                              id: generateId(),
                              type: "paragraph" as ElementType,
                              content: "✅",
                              styles: { fontSize: "24px", margin: 0 },
                            },
                            {
                              id: generateId(),
                              type: "paragraph" as ElementType,
                              content:
                                "A personal email or SMS in the next 24-48 hours to book your slot",
                              styles: {
                                color: "#231F20",
                                fontSize: "28px",
                                fontWeight: 400,
                                margin: 0,
                              },
                              responsiveStyles: {
                                tablet: { fontSize: "24px" },
                                mobile: {
                                  fontSize: "18px",
                                  lineHeight: "26px",
                                },
                              },
                            },
                          ],
                        },
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          styles: {
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          },
                          children: [
                            {
                              id: generateId(),
                              type: "paragraph" as ElementType,
                              content: "✅",
                              styles: { fontSize: "24px", margin: 0 },
                            },
                            {
                              id: generateId(),
                              type: "paragraph" as ElementType,
                              content:
                                "Tips on getting started with your protocol",
                              styles: {
                                color: "#231F20",
                                fontSize: "28px",
                                fontWeight: 400,
                                margin: 0,
                              },
                              responsiveStyles: {
                                tablet: { fontSize: "24px" },
                                mobile: {
                                  fontSize: "18px",
                                  lineHeight: "26px",
                                },
                              },
                            },
                          ],
                        },
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          styles: {
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          },
                          children: [
                            {
                              id: generateId(),
                              type: "paragraph" as ElementType,
                              content: "✅",
                              styles: { fontSize: "24px", margin: 0 },
                            },
                            {
                              id: generateId(),
                              type: "paragraph" as ElementType,
                              content: "Answers to any burning questions",
                              styles: {
                                color: "#231F20",
                                fontSize: "28px",
                                fontWeight: 400,
                                margin: 0,
                              },
                              responsiveStyles: {
                                tablet: { fontSize: "24px" },
                                mobile: {
                                  fontSize: "18px",
                                  lineHeight: "26px",
                                },
                              },
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
        // Headline
        {
          id: generateId(),
          type: "container" as ElementType,
          styles: {
            padding: "32px 19px",
            borderRadius: "32px",
            color: "#000",
            fontFamily: "SF Pro Display",
            fontSize: "40.3px",
            fontStyle: "italic",
            fontWeight: 900,
            lineHeight: "100%",
            border: "1px solid #808285",
            backgroundImage:
              "linear-gradient(90deg, #E6E7E8 10.18%, #F0F0F1 42.91%, #FCFCFC 72%, #FFFFFF 80%)",
            marginTop: "0px",
            width: "100%",
            textAlign: "center",
          },
          responsiveStyles: {
            tablet: { fontSize: "26px" },
            mobile: { padding: "18px 12px", fontSize: "20px" },
          },
          children: [
            {
              id: generateId(),
              type: "paragraph" as ElementType,
              content:
                "This is your moment - let's make your transformation unstoppable.",
              styles: {
                color: "#000",
                fontFamily: "SF Pro Display",
                fontSize: "40.3px",
                fontStyle: "italic",
                fontWeight: 900,
                lineHeight: "100%",
                margin: 0,
              },
              responsiveStyles: {
                tablet: { fontSize: "26px" },
                mobile: { fontSize: "20px" },
              },
            },
          ],
        },
      ],
    },
  ],
});

const createScientificReferencesSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as ElementType,
  styles: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    margin: "100px 0 72px 0",
    backgroundColor: "background",
  },
  responsiveStyles: {
    tablet: { margin: "72px 0 36px 0" },
    mobile: { margin: "40px 0 32px 0" },
  },
  children: [
    {
      id: generateId(),
      type: "container" as ElementType,
      styles: {
        maxWidth: "1334px",
        margin: "0 auto",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "0px",
      },
      responsiveStyles: {
        tablet: { gap: "18px" },
        mobile: { gap: "12px" },
      },
      children: [
        {
          id: generateId(),
          type: "paragraph" as ElementType,
          content: "Scientific References:",
          styles: {
            color: "#000",
            textAlign: "center",
            fontFamily: "Poppins, sans-serif",
            fontSize: "34px",
            fontWeight: 400,
            lineHeight: "80px",
            letterSpacing: "-1.35px",
            margin: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
          responsiveStyles: {
            mobile: { fontSize: "22px", lineHeight: "25px" },
          },
        },
        {
          id: generateId(),
          type: "container" as ElementType,
          styles: {
            width: "100%",
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            alignItems: "center",
            gap: "35px",
            height: "100px",
          },
          responsiveStyles: {
            tablet: { gap: "24px", height: "auto" },
            mobile: { gap: "20px", height: "auto" },
          },
          children: [
            ...[1, 2, 3, 4].map((i) => ({
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                maxWidth: "258px",
                width: "100%",
              },
              responsiveStyles: {
                tablet: { maxWidth: "220px" },
                mobile: { maxWidth: "167px" },
              },
              children: [
                {
                  id: generateId(),
                  type: "image" as ElementType,
                  src: "/fortigold/scientificReferencesSection1.png",
                  alt: `Reference Logo ${i}`,
                  styles: {
                    width: "100%",
                    objectFit: "contain",
                  },
                },
              ],
            })),
          ],
        },
      ],
    },
  ],
});

const createScientificRefListingSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as ElementType,
  styles: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    margin: "60px 0 72px 0",
    backgroundColor: "background",
  },
  responsiveStyles: {
    tablet: { margin: "32px 0" },
    mobile: { margin: "24px 0" },
  },
  children: [
    {
      id: generateId(),
      type: "container" as ElementType,
      styles: {
        maxWidth: "1334px",
        margin: "0 auto",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "0px",
      },
      responsiveStyles: {
        tablet: { gap: "18px", maxWidth: "95%" },
        mobile: { gap: "12px", maxWidth: "95%" },
      },
      children: [
        {
          id: generateId(),
          type: "container" as ElementType,
          styles: {
            width: "100%",
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "flex-start",
            gap: "24px",
          },
          responsiveStyles: {
            tablet: { gap: "18px", flexDirection: "column" },
            mobile: { gap: "12px", flexDirection: "column" },
          },
          children: [
            // First Col
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                width: "calc(50% - 12px)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "flex-start",
                gap: "16px",
              },
              responsiveStyles: {
                tablet: { width: "100%" },
                mobile: { width: "100%" },
              },
              children: [
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    display: "inline",
                    width: "100%",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content:
                        "<b>Campos, M.</b> (2022). Leaky Gut: What is it and What Does it Mean for Your Health? Harvard Health Blog. Retrieved from <a href='https://www.health.harvard.edu/blog/leaky-gut-what-is-it-and-what-does-it-mean-for-you-2017092212451' target='_blank' style='text-decoration: underline;'>https://www.health.harvard.edu/blog/leaky-gut-what-is-it-and-what-does-it-mean-for-you-2017092212451</a>",
                      styles: {
                        color: "rgba(0, 0, 0, 0.70)",
                        fontSize: "18px",
                        fontFamily: "Poppins, sans-serif",
                        margin: 0,
                      },
                    },
                  ],
                },
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    display: "inline",
                    width: "100%",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content:
                        "<b>Campos, M.</b> (2022). Leaky Gut: What is it and What Does it Mean for Your Health? Harvard Health Blog. Retrieved from <a href='https://www.health.harvard.edu/blog/leaky-gut-what-is-it-and-what-does-it-mean-for-you-2017092212451' target='_blank' style='text-decoration: underline;'>https://www.health.harvard.edu/blog/leaky-gut-what-is-it-and-what-does-it-mean-for-you-2017092212451</a>",
                      styles: {
                        color: "rgba(0, 0, 0, 0.70)",
                        fontSize: "18px",
                        fontFamily: "Poppins, sans-serif",
                        margin: 0,
                      },
                    },
                  ],
                },
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    display: "inline",
                    width: "100%",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content:
                        "<b>Campos, M.</b> (2022). Leaky Gut: What is it and What Does it Mean for Your Health? Harvard Health Blog. Retrieved from <a href='https://www.health.harvard.edu/blog/leaky-gut-what-is-it-and-what-does-it-mean-for-you-2017092212451' target='_blank' style='text-decoration: underline;'>https://www.health.harvard.edu/blog/leaky-gut-what-is-it-and-what-does-it-mean-for-you-2017092212451</a>",
                      styles: {
                        color: "rgba(0, 0, 0, 0.70)",
                        fontSize: "18px",
                        fontFamily: "Poppins, sans-serif",
                        margin: 0,
                      },
                    },
                  ],
                },
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    display: "inline",
                    width: "100%",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content:
                        "<b>Campos, M.</b> (2022). Leaky Gut: What is it and What Does it Mean for Your Health? Harvard Health Blog. Retrieved from <a href='https://www.health.harvard.edu/blog/leaky-gut-what-is-it-and-what-does-it-mean-for-you-2017092212451' target='_blank' style='text-decoration: underline;'>https://www.health.harvard.edu/blog/leaky-gut-what-is-it-and-what-does-it-mean-for-you-2017092212451</a>",
                      styles: {
                        color: "rgba(0, 0, 0, 0.70)",
                        fontSize: "18px",
                        fontFamily: "Poppins, sans-serif",
                        margin: 0,
                      },
                    },
                  ],
                },
              ],
            },
            // Second Col
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                width: "calc(50% - 12px)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "flex-start",
                gap: "16px",
              },
              responsiveStyles: {
                tablet: { width: "100%" },
                mobile: { width: "100%" },
              },
              children: [
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    display: "inline",
                    width: "100%",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content:
                        "<b>Campos, M.</b> (2022). Leaky Gut: What is it and What Does it Mean for Your Health? Harvard Health Blog. Retrieved from <a href='https://www.health.harvard.edu/blog/leaky-gut-what-is-it-and-what-does-it-mean-for-you-2017092212451' target='_blank' style='text-decoration: underline;'>https://www.health.harvard.edu/blog/leaky-gut-what-is-it-and-what-does-it-mean-for-you-2017092212451</a>",
                      styles: {
                        color: "rgba(0, 0, 0, 0.70)",
                        fontSize: "18px",
                        fontFamily: "Poppins, sans-serif",
                        margin: 0,
                      },
                    },
                  ],
                },
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    display: "inline",
                    width: "100%",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content:
                        "<b>Campos, M.</b> (2022). Leaky Gut: What is it and What Does it Mean for Your Health? Harvard Health Blog. Retrieved from <a href='https://www.health.harvard.edu/blog/leaky-gut-what-is-it-and-what-does-it-mean-for-you-2017092212451' target='_blank' style='text-decoration: underline;'>https://www.health.harvard.edu/blog/leaky-gut-what-is-it-and-what-does-it-mean-for-you-2017092212451</a>",
                      styles: {
                        color: "rgba(0, 0, 0, 0.70)",
                        fontSize: "18px",
                        fontFamily: "Poppins, sans-serif",
                        margin: 0,
                      },
                    },
                  ],
                },
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    display: "inline",
                    width: "100%",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content:
                        "<b>Campos, M.</b> (2022). Leaky Gut: What is it and What Does it Mean for Your Health? Harvard Health Blog. Retrieved from <a href='https://www.health.harvard.edu/blog/leaky-gut-what-is-it-and-what-does-it-mean-for-you-2017092212451' target='_blank' style='text-decoration: underline;'>https://www.health.harvard.edu/blog/leaky-gut-what-is-it-and-what-does-it-mean-for-you-2017092212451</a>",
                      styles: {
                        color: "rgba(0, 0, 0, 0.70)",
                        fontSize: "18px",
                        fontFamily: "Poppins, sans-serif",
                        margin: 0,
                      },
                    },
                  ],
                },
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    display: "inline",
                    width: "100%",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content:
                        "<b>Campos, M.</b> (2022). Leaky Gut: What is it and What Does it Mean for Your Health? Harvard Health Blog. Retrieved from <a href='https://www.health.harvard.edu/blog/leaky-gut-what-is-it-and-what-does-it-mean-for-you-2017092212451' target='_blank' style='text-decoration: underline;'>https://www.health.harvard.edu/blog/leaky-gut-what-is-it-and-what-does-it-mean-for-you-2017092212451</a>",
                      styles: {
                        color: "rgba(0, 0, 0, 0.70)",
                        fontSize: "18px",
                        fontFamily: "Poppins, sans-serif",
                        margin: 0,
                      },
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ],
});

const createFooterMenuSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as ElementType,
  styles: {
    backgroundColor: "primary",
    width: "100%",
    display: "flex",
    justifyContent: "center",
  },
  children: [
    {
      id: generateId(),
      type: "container" as ElementType,
      styles: {
        maxWidth: "100%",
        margin: "0 auto",
        width: "100%",
        display: "flex",
        justifyContent: "center",
      },
      children: [
        {
          id: generateId(),
          type: "container" as ElementType,
          styles: {
            height: "100px",
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            alignItems: "center",
            gap: "0 72px",
            width: "100%",
          },
          responsiveStyles: {
            tablet: {
              gap: "0 24px",
            },
            mobile: {
              flexDirection: "column",
              gap: "28px 16px",
              height: "auto",
              padding: "28px 0",
            },
          },
          children: [
            // Privacy
            {
              id: generateId(),
              type: "link" as ElementType,
              content: "Privacy",
              href: "/privacy-policy",
              styles: {
                color: "#FFF",
                textAlign: "center",
                fontFamily: "Roboto, sans-serif",
                fontSize: "24px",
                fontStyle: "normal",
                fontWeight: 900,
                letterSpacing: "-1.05px",
                transition: "transform 0.2s cubic-bezier(.4,0,.2,1)",
                textDecoration: "none",
                display: "block",
              },
              hoverStyles: {
                transform: "translateY(-5px)",
              },
              responsiveStyles: {
                mobile: {
                  fontWeight: 700,
                },
              },
            },
            // Terms & Conditions
            {
              id: generateId(),
              type: "link" as ElementType,
              content: "Terms & Conditions",
              href: "/terms-and-conditions",
              styles: {
                color: "#FFF",
                textAlign: "center",
                fontFamily: "Roboto, sans-serif",
                fontSize: "24px",
                fontStyle: "normal",
                fontWeight: 900,
                letterSpacing: "-1.05px",
                transition: "transform 0.2s cubic-bezier(.4,0,.2,1)",
                textDecoration: "none",
                display: "block",
              },
              hoverStyles: {
                transform: "translateY(-5px)",
              },
              responsiveStyles: {
                mobile: {
                  fontWeight: 700,
                },
              },
            },
            // Disclaimer
            {
              id: generateId(),
              type: "link" as ElementType,
              content: "Disclaimer",
              href: "/disclaimer",
              styles: {
                color: "#FFF",
                textAlign: "center",
                fontFamily: "Roboto, sans-serif",
                fontSize: "24px",
                fontStyle: "normal",
                fontWeight: 900,
                letterSpacing: "-1.05px",
                transition: "transform 0.2s cubic-bezier(.4,0,.2,1)",
                textDecoration: "none",
                display: "block",
              },
              hoverStyles: {
                transform: "translateY(-5px)",
              },
              responsiveStyles: {
                mobile: {
                  fontWeight: 700,
                },
              },
            },
            // Product Label (trigger, not a link)
            {
              id: generateId(),
              type: "button" as ElementType,
              content: "Product Label",
              // href: "#product-label",
              styles: {
                color: "#FFF",
                textAlign: "center",
                fontFamily: "Roboto, sans-serif",
                fontSize: "24px",
                fontStyle: "normal",
                fontWeight: 900,
                letterSpacing: "-1.05px",
                transition: "transform 0.2s cubic-bezier(.4,0,.2,1)",
                textDecoration: "none",
                display: "block",
                cursor: "pointer",
              },
              hoverStyles: {
                transform: "translateY(-5px)",
              },
              responsiveStyles: {
                mobile: {
                  fontWeight: 700,
                },
              },
              triggerPopup: "fortigold-label-popup",
              // You can add a custom property for JS trigger if needed
            },
            // Returns & Refunds
            {
              id: generateId(),
              type: "link" as ElementType,
              content: "Returns & Refunds",
              href: "/returns-and-refunds",
              styles: {
                color: "#FFF",
                textAlign: "center",
                fontFamily: "Roboto, sans-serif",
                fontSize: "24px",
                fontStyle: "normal",
                fontWeight: 900,
                letterSpacing: "-1.05px",
                transition: "transform 0.2s cubic-bezier(.4,0,.2,1)",
                textDecoration: "none",
                display: "block",
              },
              hoverStyles: {
                transform: "translateY(-5px)",
              },
              responsiveStyles: {
                mobile: {
                  fontWeight: 700,
                },
              },
            },
            // Contact
            {
              id: generateId(),
              type: "link" as ElementType,
              content: "Contact",
              href: "/contact-us",
              styles: {
                color: "#FFF",
                textAlign: "center",
                fontFamily: "Roboto, sans-serif",
                fontSize: "24px",
                fontStyle: "normal",
                fontWeight: 900,
                letterSpacing: "-1.05px",
                transition: "transform 0.2s cubic-bezier(.4,0,.2,1)",
                textDecoration: "none",
                display: "block",
              },
              hoverStyles: {
                transform: "translateY(-5px)",
              },
              responsiveStyles: {
                mobile: {
                  fontWeight: 700,
                },
              },
            },
          ],
        },
      ],
    },
  ],
});

const createFooterEndSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as ElementType,
  styles: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    backgroundColor: "background",
    margin: 0,
    padding: 0,
  },
  children: [
    {
      id: generateId(),
      type: "container" as ElementType,
      styles: {
        maxWidth: "1200px",
        margin: "0 auto",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        gap: "12px",
        padding: "60px 0 0",
      },
      responsiveStyles: {
        mobile: {
          padding: "0 15px",
          margin: "30px auto 0",
        },
      },
      children: [
        {
          id: generateId(),
          type: "paragraph" as ElementType,
          content:
            "These statements have not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease. The information presented on this site is for informational purposes only and is not a substitute for advice from your physician or other healthcare professional. Always consult with a qualified healthcare provider before beginning any diet, supplement, or exercise program.",
          styles: {
            textAlign: "center",
            color: "rgba(0, 0, 0, 0.60)",
            fontFamily: "Poppins, sans-serif",
            fontSize: "24px",
            fontStyle: "normal",
            fontWeight: 400,
            lineHeight: "28px",
            letterSpacing: "-0.28px",
          },
          responsiveStyles: {
            mobile: {
              fontSize: "16px",
            },
          },
        },
        {
          id: generateId(),
          type: "paragraph" as ElementType,
          content:
            "Individual results may vary. Testimonials and reviews appearing on this site reflect real-life experiences of individuals who used our products. However, results may not be typical and are not guaranteed.",
          styles: {
            textAlign: "center",
            color: "rgba(0, 0, 0, 0.60)",
            fontFamily: "Poppins, sans-serif",
            fontSize: "24px",
            fontStyle: "normal",
            fontWeight: 400,
            lineHeight: "28px",
            letterSpacing: "-0.28px",
          },
          responsiveStyles: {
            mobile: {
              fontSize: "16px",
            },
          },
        },
        {
          id: generateId(),
          type: "paragraph" as ElementType,
          content: "Copyright © 2024 – 2025 FortiGold. All Rights Reserved.",
          styles: {
            padding: "30px 0",
            color: "rgba(0, 0, 0, 0.60)",
            textAlign: "center",
            fontFamily: "Poppins, sans-serif",
            fontSize: "22px",
            fontStyle: "normal",
            fontWeight: 400,
            lineHeight: "45px",
            letterSpacing: "-0.22px",
          },
          responsiveStyles: {
            tablet: {
              padding: "10px 0",
              fontSize: "12px",
            },
            mobile: {
              padding: "10px 0",
              fontSize: "12px",
            },
          },
        },
      ],
    },
  ],
});

export const template1Thankyou = (globalConfig: GlobalConfig) => {
  return {
    thankYouHeaderSection: createThankYouHeaderSection(globalConfig),
    thankYouImageHeaderSection: createThankYouImageHeaderSection(globalConfig),
    thankYouCardsSection: createThankYouCardsSection(globalConfig),
    thankYouNewsletterSection: createThankYouNewsletterSection(globalConfig),
    getReadySection: createGetReadySection(globalConfig),
    scientificReferencesSection:
      createScientificReferencesSection(globalConfig),
    scientificRefListingSection:
      createScientificRefListingSection(globalConfig),
    footerMenuSection: createFooterMenuSection(globalConfig),
    footerEndSection: createFooterEndSection(globalConfig),
  };
};
